import { Badge } from '@/components/ui/badge';

/**
 * Shared, presentation-only helpers for the Apple Pay BYOK surfaces (the
 * account-manager Certificates tab and the merchant self-serve settings page).
 * No hooks, no data fetching — safe to import from either surface.
 */

/** Read a File into a base64 string (for .p12 upload — bytes never touch our DB). */
export async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Days until a date (negative if past), or null when unset/invalid. */
export function daysUntil(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

const STATUS_LABELS: Record<string, string> = {
  pending_certs: 'Pending certificates',
  pending_domain: 'Pending domain',
  active: 'Active',
  expired: 'Expired',
  disabled: 'Disabled',
};

export function RegistrationStatusBadge({ status }: { status: string }) {
  const label = STATUS_LABELS[status] ?? status;
  if (status === 'active') {
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{label}</Badge>;
  }
  if (status === 'expired' || status === 'disabled') {
    return <Badge variant="destructive">{label}</Badge>;
  }
  return <Badge variant="secondary">{label}</Badge>;
}

/** Expiry badge: muted > 30d, amber ≤ 30d, red once expired. */
export function ExpiryBadge({ value }: { value: string | Date | null | undefined }) {
  const days = daysUntil(value);
  if (days === null) return <span className="text-muted-foreground">—</span>;
  if (days < 0) {
    return <Badge variant="destructive">Expired {formatDate(value)}</Badge>;
  }
  if (days <= 30) {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
        {days}d left · {formatDate(value)}
      </Badge>
    );
  }
  return <span className="text-muted-foreground text-sm">{formatDate(value)}</span>;
}
